import { MessageCircle } from "lucide-react"

import { CommentCard } from "@/components/phrases/comment-card"
import { CommentForm } from "@/components/phrases/comment-form"
import { cn } from "@/lib/utils"
import type { Comment, CommentUser } from "@/types/phrase"

interface CommentSectionProps {
  phraseSlug: string
  comments: Comment[]
  currentUser?: CommentUser | null
  className?: string
}

export function CommentSection({
  phraseSlug,
  comments,
  currentUser,
  className,
}: CommentSectionProps) {
  return (
    <div className={cn("space-y-6", className)}>
      {/* Section header */}
      <div className="flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/50">
          <MessageCircle className="size-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Discussion
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {comments.length === 0
              ? "Be the first to share your thoughts"
              : `${comments.length} comment${comments.length === 1 ? "" : "s"}`}
          </p>
        </div>
      </div>

      {/* Comment form */}
      <CommentForm phraseSlug={phraseSlug} currentUser={currentUser} />

      {/* Comments list */}
      {comments.length > 0 && (
        <div className="space-y-3">
          {comments.map((comment, index) => (
            <div
              key={comment.id}
              className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both"
              style={{ animationDelay: `${Math.min(index * 40, 300)}ms` }}
            >
              <CommentCard comment={comment} phraseSlug={phraseSlug} isAuthenticated={!!currentUser} />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {comments.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center dark:border-slate-800 dark:bg-slate-900/30">
          <MessageCircle className="mx-auto mb-3 size-8 text-slate-300 dark:text-slate-600" />
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            No comments yet
          </p>
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            Share a tip, ask a question, or record your pronunciation
          </p>
        </div>
      )}
    </div>
  )
}
